<template>
    <div class="stage-layout">
        <!-- Header -->
        <header class="stage-header">
            <div class="header-logo">
                <div class="logo-box">MUIC</div>
                <div class="logo-text">
                    <span class="logo-title">MUIC</span>
                    <span class="logo-subtitle">ORIENTATION CHECK-IN</span>
                </div>
            </div>
            <div class="header-status">
                <span class="status-dot"></span> CHECK-IN OPEN
            </div>
        </header>

        <div class="mt-20 stage-content">
            <div class="top-group">
                <!-- Top Section -->
                <div class="top-section">
                    <!-- Left: Info & Timer -->
                    <div class="info-section">
                        <div class="muic-badge">
                            <a-icon type="safety-certificate" /> {{ accountType }}
                        </div>

                        <!-- <p class="room-title">Orientation {{ roomName }}</p> -->

                        <h1 class="main-heading">
                            Scan.<br />
                            Sign in You're done.
                        </h1>

                        <p class="description">
                            Successful check-in gives you one entry in this room's prize draw.
                        </p>

                        <div class="timer-box">
                            <div class="timer-header">
                                <a-icon type="clock-circle" /> ROUND TIMER
                            </div>
                            <div class="timer-display">{{ formattedTime }}</div>
                            <div class="timer-status">{{ timerStatusText }}</div>
                            <div class="timer-actions"
                                style="display: flex; justify-content: center; gap: 10px; margin-top: 15px; flex-wrap: wrap;">
                                <button class="btn btn-purple" @click="controlTimer('start')"
                                    v-if="timer.status !== 'running'"><a-icon type="caret-right" /> Start</button>
                                <button class="btn btn-yellow" @click="controlTimer('pause')"
                                    v-if="timer.status === 'running'"><a-icon type="pause" /> Pause</button>
                                <button class="btn btn-outline" @click="controlTimer('restart')"><a-icon
                                        type="reload" />
                                    Restart</button>
                                <button class="btn btn-outline" @click="controlTimer('reset')"><a-icon type="stop" />
                                    Reset</button>
                            </div>
                        </div>
                    </div>

                    <!-- Right: QR Code -->
                    <div class="qr-section">
                        <div class="qr-card">
                            <div class="qr-title">
                                <a-icon type="scan" /> USE YOUR PHONE CAMERA<br />
                                <span class="qr-subtitle">Scan to check in</span>
                            </div>

                            <div class="qr-code-wrapper">
                                <qr-code :text="qrUrl" :size="350" color="#3e247a" bg-color="#ffffff"></qr-code>
                            </div>

                            <div class="qr-url-box">
                                <span class="url-label">OR VISIT</span><br />
                                <span class="url-text">{{ displayUrl }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Staff Controls Section -->
                <div class="staff-controls-section">

                    <div class="stats-row"
                        style="margin-top: 10px; display: grid; grid-template-columns: repeat(5, 1fr); gap: 15px;">

                        <div class="stat-card" style="background: #f6ffed; border: 1px solid #b7eb8f;">
                            <div class="stat-icon-wrapper" style="background: #d9f7be; color: #52c41a;">
                                <a-icon type="check-circle" />
                            </div>
                            <div class="stat-info">
                                <div class="stat-label" style="color: #52c41a;">Arrived</div>
                                <div class="stat-value" style="color: #52c41a;">{{ metrics.arrived }}</div>
                            </div>
                        </div>

                        <div class="stat-card">
                            <div class="stat-icon-wrapper purple">
                                <a-icon type="team" />
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Expected</div>
                                <div class="stat-value">{{ metrics.expected }}</div>
                            </div>
                        </div>

                        <div class="stat-card">
                            <div class="stat-icon-wrapper" style="background: #fff1f0; color: #f5222d;">
                                <a-icon type="usergroup-delete" />
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Remaining</div>
                                <div class="stat-value">{{ metrics.remaining }}</div>
                            </div>
                        </div>

                        <div class="stat-card yellow-bg">
                            <div class="stat-icon-wrapper yellow">
                                <a-icon type="ticket" />
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Eligible</div>
                                <div class="stat-value">{{ metrics.eligible_for_draw }}</div>
                            </div>
                        </div>

                        <div class="stat-card">
                            <div class="stat-icon-wrapper" style="background: #e6f7ff; color: #1890ff;">
                                <a-icon type="trophy" />
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Winners</div>
                                <div class="stat-value">{{ metrics.winner_count }}</div>
                            </div>
                        </div>
                    </div>

                    <div style="display: flex; justify-content: center; margin-top: 15px; margin-bottom: 15px;">
                        <button class="btn btn-yellow" @click="drawWinners" :disabled="isDrawing"
                            :class="{ 'is-drawing-btn': isDrawing }"
                            style="font-size: 20px; padding: 15px 50px; border-radius: 12px; box-shadow: 0 10px 20px rgba(255, 212, 59, 0.3); transition: all 0.3s;">
                            <a-icon type="sync" spin v-if="isDrawing" />
                            <a-icon type="trophy" v-else />
                            {{ isDrawing ? 'Drawing Winners...' : 'Draw 16 winners' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Live Prize Draw Section -->
            <div class="prize-draw-section">
                <div class="section-label">LIVE PRIZE DRAW</div>
                <div class="prize-header">
                    <h2 class="prize-title">Winners</h2>
                    <a-icon type="trophy" class="prize-trophy-icon" />
                </div>

                <!-- Drawing State -->
                <div v-if="isDrawing" class="drawing-animation-box">
                    <div class="draw-icons">
                        <a-icon type="trophy" class="pulsing-trophy" />
                        <div class="magic-sparkles">✨</div>
                    </div>
                    <div class="draw-text">Finding lucky winners...</div>
                </div>

                <!-- Winners List -->
                <div v-else-if="winners && winners.length > 0" class="winners-grid">
                    <div v-for="(winner, index) in winners" :key="index" class="winner-card"
                        :style="{ animationDelay: `${index * 0.15}s` }">
                        <div class="winner-rank">{{ winner.rank || index + 1 }}</div>
                        <div class="winner-details">
                            <div class="winner-id">{{ winner.student_id || winner.id || 'Unknown ID' }}</div>
                            <div class="winner-name">{{ winner.full_name || winner.name || winner.email || 'Winner' }}
                            </div>
                        </div>
                        <a-icon type="gift" class="winner-gift" />
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <a-icon type="trophy" class="empty-icon" />
                    <div class="empty-text">
                        <strong>Waiting for the draw</strong><br />
                        Winners will appear here automatically.
                    </div>
                </div>
            </div>
        </div>

        <!-- Invalid URL Modal -->
        <a-modal v-model="invalidUrlModalVisible" title="Invalid URL" :closable="false" :maskClosable="false"
            :footer="null" :keyboard="false">
            <div style="text-align: center; padding: 20px 0;">
                <a-icon type="warning" style="font-size: 48px; color: #faad14; margin-bottom: 20px;" />
                <h3 style="color: #333; font-weight: bold;">URL ระบบไม่ถูกต้อง</h3>
                <p style="color: #666; margin-bottom: 0;">กรุณาตรวจสอบลิงก์อีกครั้ง (ต้องระบุ room และ type)</p>
            </div>
        </a-modal>
    </div>
</template>

<script>
import VueQRCodeComponent from 'vue-qrcode-component';
import axios from 'axios';

export default {
    name: 'OrientationStage',
    components: {
        'qr-code': VueQRCodeComponent
    },
    data() {
        return {
            roomName: 'ROOM1',
            accountType: 'MUIC Account',
            pollingInterval: null,
            isDrawing: false,
            winners: [],
            metrics: {
                expected: 0,
                arrived: 0,
                remaining: 0,
                eligible_for_draw: 0,
                winner_count: 0
            },
            timer: {
                status: 'idle',
                remaining_seconds: 600,
                duration_seconds: 600,
                ends_at: null
            },
            localRemainingSeconds: 600,
            localTimerInterval: null,
            invalidUrlModalVisible: false
        }
    },
    computed: {
        studentUrl() {
            // Use newmy.muic.io for prod, or localhost origin for local testing
            const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const base = isLocal ? window.location.origin : 'https://newmy.muic.io';

            // Ensure no spaces in type
            const type = (this.$route.query.type || 'MUICAccount').replace(/\s+/g, '');

            return `${base}/orientation?type=${type}`;
        },
        displayUrl() {
            return this.studentUrl;
        },
        qrUrl() {
            return this.studentUrl;
        },
        formattedTime() {
            const minutes = Math.floor(this.localRemainingSeconds / 60);
            const seconds = this.localRemainingSeconds % 60;
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        },
        timerStatusText() {
            if (this.timer.status === 'running') return 'RUNNING';
            if (this.timer.status === 'paused') return 'PAUSED';
            return 'READY';
        }
    },
    created() {
        // Read query parameters
        if (!this.$route.query.room || !this.$route.query.type) {
            this.invalidUrlModalVisible = true;
        } else {
            this.roomName = this.$route.query.room;
            if (this.$route.query.type === 'MUAccount') {
                this.accountType = 'MU Account';
            } else if (this.$route.query.type === 'MUICAccount') {
                this.accountType = 'MUIC Account';
            } else {
                this.accountType = this.$route.query.type;
            }
        }
    },
    mounted() {
        this.fetchStageData();
        // Poll every 3 seconds (3000 ms)
        this.pollingInterval = setInterval(() => {
            this.fetchStageData();
        }, 3000);
    },
    beforeDestroy() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }
        this.stopLocalTimer();
    },
    methods: {
        async fetchStageData() {
            const roomId = this.$route.query.room;
            if (roomId && !this.isDrawing) { // Don't poll while drawing to avoid glitching
                try {
                    const response = await axios.get(`/orientation-game/api/v1/staff/rooms/${roomId}/stage/`);
                    if (response.data && response.data.metrics) {
                        this.metrics = response.data.metrics;
                    }
                    if (response.data && response.data.winners && Array.isArray(response.data.winners)) {
                        this.winners = response.data.winners;
                    }
                    if (response.data && response.data.timer) {
                        this.timer = response.data.timer;
                        this.syncLocalTimer();
                    }
                } catch (error) {
                    console.error("Error fetching stage data:", error);
                }
            }
        },
        syncLocalTimer() {
            if (this.timer.status === 'running') {
                if (this.timer.ends_at) {
                    const endsAt = new Date(this.timer.ends_at).getTime();
                    const now = new Date().getTime();
                    const remaining = Math.max(0, Math.floor((endsAt - now) / 1000));
                    this.localRemainingSeconds = remaining;
                } else {
                    this.localRemainingSeconds = this.timer.remaining_seconds;
                }
                this.startLocalTimer();
            } else {
                this.localRemainingSeconds = this.timer.remaining_seconds;
                this.stopLocalTimer();
            }
        },
        startLocalTimer() {
            this.stopLocalTimer();
            this.localTimerInterval = setInterval(() => {
                if (this.localRemainingSeconds > 0) {
                    this.localRemainingSeconds--;
                } else {
                    this.stopLocalTimer();
                }
            }, 1000);
        },
        stopLocalTimer() {
            if (this.localTimerInterval) {
                clearInterval(this.localTimerInterval);
                this.localTimerInterval = null;
            }
        },
        async controlTimer(action) {
            const roomId = this.$route.query.room;
            if (!roomId) return;
            try {
                // Post to API to control timer
                await axios.post(`/orientation-game/api/v1/staff/rooms/${roomId}/timer/`, { action });
                // Fetch updated state immediately
                await this.fetchStageData();
            } catch (error) {
                console.error("Error controlling timer:", error);
                this.$message.error('Failed to control timer.');
            }
        },
        async drawWinners() {
            if (this.isDrawing) return;
            const roomId = this.$route.query.room;
            if (!roomId) return;

            this.isDrawing = true;
            this.winners = [];

            // Force a 3-second minimum delay for suspense/animation
            const suspensePromise = new Promise(resolve => setTimeout(resolve, 3000));

            try {
                const responsePromise = axios.post(`/orientation-game/api/v1/staff/rooms/${roomId}/draw/`, { count: 16 });

                // Wait for both the API and the animation delay
                const [response] = await Promise.all([responsePromise, suspensePromise]);

                // Usually returns { data: [ { student_id, name... }, ... ] } or similar
                if (response.data) {
                    // Try to handle both array and object wrapping
                    this.winners = Array.isArray(response.data) ? response.data : (response.data.winners || response.data.data || []);
                }

                // Fetch metrics immediately after drawing
                await this.fetchStageData();
            } catch (error) {
                console.error("Draw error:", error);
                // Also wait for suspense if error happened quickly
                await suspensePromise;
                this.$message.error('Failed to draw winners. Make sure you have eligible people in the room.');
            } finally {
                this.isDrawing = false;
            }
        }
    }
}
</script>

<style scoped>
.stage-layout {
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #fff9e6 0%, #ffffff 50%, #f0f7fa 100%);
    padding: 15px 40px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Header */
.stage-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.header-logo {
    display: flex;
    align-items: center;
    gap: 12px;
}

.logo-box {
    background: #3e247a;
    color: white;
    font-weight: 900;
    padding: 6px;
    border-radius: 8px;
    font-size: 14px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-text {
    display: flex;
    flex-direction: column;
}

.logo-title {
    color: #3e247a;
    font-weight: 800;
    font-size: 16px;
    line-height: 1.2;
}

.logo-subtitle {
    color: #3e247a;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
}

.header-status {
    background: #e6f6eb;
    color: #118641;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 8px;
    letter-spacing: 1px;
}

.status-dot {
    width: 8px;
    height: 8px;
    background: #118641;
    border-radius: 50%;
}

.stage-content {
    max-width: 1200px;
    margin: 0 auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 20px;
    width: 100%;
}

.top-group {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

/* Top Section */
.top-section {
    display: flex;
    gap: 30px;
    margin-bottom: 0;
}

.info-section {
    flex: 1;
}

.muic-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f0ebf8;
    color: #3e247a;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 15px;
}

.room-title {
    color: #3e247a;
    font-weight: 700;
    margin-bottom: 12px;
    font-size: 16px;
}

.main-heading {
    color: #3e247a;
    font-size: 36px;
    font-weight: 900;
    line-height: 1;
    margin-bottom: 10px;
    letter-spacing: -1.5px;
}

.description {
    color: #666;
    font-size: 16px;
    margin-bottom: 15px;
    line-height: 1.3;
}

/* Timer Box */
.timer-box {
    background: #fcfbfe;
    border: 1px solid #eaeaea;
    border-radius: 24px;
    padding: 15px 25px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
}

.timer-header {
    color: #3e247a;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 1px;
    margin-bottom: 5px;
}

.timer-display {
    color: #3e247a;
    font-size: 56px;
    font-weight: 900;
    line-height: 1;
    margin-bottom: 5px;
    letter-spacing: -2px;
}

.timer-status {
    color: #666;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
}

/* QR Section */
.qr-section {
    flex: 1;
    display: flex;
    justify-content: flex-end;
}

.qr-card {
    background: #472d8a;
    border-radius: 32px;
    padding: 15px 20px;
    text-align: center;
    width: 100%;
    max-width: 450px;
    box-shadow: 0 20px 50px rgba(71, 45, 138, 0.2);
}

.qr-title {
    color: #ffd43b;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1.5px;
    margin-bottom: 5px;
}

.qr-subtitle {
    color: white;
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.5px;
    display: block;
    margin-top: 2px;
}

.qr-code-wrapper {
    background: white;
    padding: 10px;
    border-radius: 20px;
    display: inline-block;
    margin-bottom: 10px;
    border: 4px solid #ffd43b;
}

.qr-url-box {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 10px;
}

.url-label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1px;
}

.url-text {
    color: white;
    font-size: 14px;
    font-weight: 700;
    margin-top: 4px;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Staff Controls Section */
.section-label {
    color: #3e247a;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1.5px;
    margin-bottom: 16px;
}

.controls-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.controls-title {
    font-size: 24px;
    font-weight: 800;
    color: #222;
    margin: 0 0 8px 0;
}

.controls-desc {
    color: #666;
    font-size: 14px;
    margin: 0;
}

.controls-actions {
    display: flex;
    gap: 12px;
}

.btn {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-purple {
    background: #3e247a;
    color: white;
}

.btn-outline {
    background: white;
    border: 1px solid #eaeaea;
    color: #333;
}

.btn-yellow {
    background: #ffd43b;
    color: #333;
}

.timer-notification {
    background: #f0ebf8;
    color: #3e247a;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 24px;
}

/* Stats Row */
.stats-row {
    margin-bottom: 0;
}

.stat-card {
    flex: 1;
    background: white;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    padding: 12px 15px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.stat-card.yellow-bg {
    background: #fffdf5;
    border-color: #f6e9c4;
}

.stat-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
}

.stat-icon-wrapper.blue {
    background: #f0f7ff;
    color: #0070f3;
}

.stat-icon-wrapper.purple {
    background: #f0ebf8;
    color: #3e247a;
}

.stat-icon-wrapper.yellow {
    background: #fffdf5;
    color: #a4731c;
    border: 1px solid #f6e9c4;
}

.stat-label {
    color: #666;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
}

.stat-value {
    color: #222;
    font-size: 32px;
    font-weight: 800;
    line-height: 1;
}

/* Prize Draw Section */
.prize-draw-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
}

.prize-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.prize-title {
    font-size: 24px;
    font-weight: 800;
    color: #222;
    margin: 0;
}

.prize-trophy-icon {
    font-size: 24px;
    color: #a4731c;
}

.empty-state {
    background: white;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    padding: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
}

.empty-icon {
    font-size: 24px;
    color: #888;
}

.empty-text {
    color: #666;
    font-size: 14px;
}

.empty-text strong {
    color: #333;
    font-size: 15px;
}

/* Animations & Draw Result Styles */
.is-drawing-btn {
    opacity: 0.8;
    transform: scale(0.98);
    pointer-events: none;
    box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.1) !important;
}

.drawing-animation-box {
    background: linear-gradient(135deg, #f0ebf8 0%, #e6f7ff 100%);
    border: 2px dashed #b37feb;
    border-radius: 16px;
    padding: 30px 20px;
    text-align: center;
    margin-bottom: 10px;
}

.draw-icons {
    position: relative;
    display: inline-block;
    margin-bottom: 20px;
}

.pulsing-trophy {
    font-size: 80px;
    color: #ffd43b;
    animation: pulseTrophy 1s infinite alternate;
    filter: drop-shadow(0 0 15px rgba(255, 212, 59, 0.6));
}

.magic-sparkles {
    position: absolute;
    top: -10px;
    right: -20px;
    font-size: 30px;
    animation: spinSparkle 2s linear infinite;
}

.draw-text {
    font-size: 24px;
    font-weight: 800;
    color: #3e247a;
    letter-spacing: 1px;
    animation: blinkText 1.5s infinite;
}

@keyframes pulseTrophy {
    0% {
        transform: scale(1);
    }

    100% {
        transform: scale(1.15);
    }
}

@keyframes spinSparkle {
    0% {
        transform: rotate(0deg) scale(1);
    }

    50% {
        transform: rotate(180deg) scale(1.2);
    }

    100% {
        transform: rotate(360deg) scale(1);
    }
}

@keyframes blinkText {
    0% {
        opacity: 0.5;
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0.5;
    }
}

.winners-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 15px;
    margin-bottom: 10px;
    overflow-y: auto;
    padding: 5px;
    padding-right: 15px; /* for scrollbar */
}

.winner-card {
    background: white;
    border: 2px solid #ffd43b;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    align-items: center;
    box-shadow: 0 8px 24px rgba(255, 212, 59, 0.15);
    opacity: 0;
    animation: popIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    position: relative;
    overflow: hidden;
}

.winner-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 0) 100%);
    animation: shimmer 2s infinite;
}

.winner-rank {
    width: 44px;
    height: 44px;
    background: #ffd43b;
    color: #3e247a;
    font-size: 20px;
    font-weight: 900;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 15px;
    flex-shrink: 0;
}

.winner-details {
    flex: 1;
}

.winner-id {
    font-size: 20px;
    font-weight: 900;
    color: #222;
    margin-bottom: 4px;
}

.winner-name {
    font-size: 14px;
    color: #666;
    font-weight: 600;
}

.winner-gift {
    font-size: 28px;
    color: #ffd43b;
    opacity: 0.3;
}

@keyframes popIn {
    0% {
        opacity: 0;
        transform: scale(0.8) translateY(20px);
    }

    100% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes shimmer {
    0% {
        left: -100%;
    }

    100% {
        left: 200%;
    }
}

/* Responsive */
@media (max-width: 1024px) {
    .top-section {
        flex-direction: column;
    }

    .main-heading {
        font-size: 60px;
    }

    .qr-section {
        justify-content: center;
    }
}
</style>
